import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import Joyride, { CallBackProps, STATUS, Step, ACTIONS } from 'react-joyride';
import { TourTooltip } from '../common/TourTooltip';
import { useAppSelector, useAppDispatch } from "../../redux/hooks";
import { updatePaperStatus, setSelectedPaperId, fetchPaperCodes } from "./EvaluatorSlice";
import EvaluatorHeader from "./EvaluatorHeader";
import AnswerSheetList from "./AnswerSheetList";
import EvaluatorSetup from "./EvaluatorSetup";
import { useEffect, useState } from "react";
import { PaperCode } from "./types";

const EvaluatorDetails = () => {
    const { paperId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useAppDispatch();

    const isSettingsMode = searchParams.get("view") === "settings";

    // Select paper from store
    const { papers, loading } = useAppSelector((state: any) => state.evaluator);
    const selectedPaper = papers.find((p: PaperCode) => p.id === paperId);

    // Set selected paper in store when mounting
    useEffect(() => {
        if (paperId) {
            dispatch(setSelectedPaperId(paperId));
        }
    }, [paperId, dispatch]);

    // Fetch papers if missing
    useEffect(() => {
        if (papers.length === 0 && !loading) {
            dispatch(fetchPaperCodes());
        }
    }, [papers.length, loading, dispatch]);

    // Tour state
    const [runTour, setRunTour] = useState(false);

    const setupTourSteps: Step[] = [
        {
            target: '.tour-setup-paper',
            title: 'Question Paper',
            content: 'Upload the question paper PDF here or view the existing one.',
            disableBeacon: true,
        },
        {
            target: '.tour-setup-rules',
            title: 'Evaluation Rules',
            content: 'Define specific marking criteria and strictness for the AI to follow.',
            disableBeacon: true,
        },
        {
            target: '.tour-setup-start',
            title: 'Start Evaluation',
            content: 'Once ready, begin the evaluation process for all student copies.',
            disableBeacon: true,
        }
    ];

    const listTourSteps: Step[] = [
        {
            target: '.tour-list-evaluate',
            title: 'Evaluate Everything',
            content: 'Select answer sheets and click here to start AI evaluation for multiple copies.',
            disableBeacon: true,
        },
        {
            target: '.tour-list-refresh',
            title: 'Refresh Status',
            content: 'Click here to check for the latest evaluation updates.',
            disableBeacon: true,
        },
        {
            target: '.tour-list-settings',
            title: 'View Settings',
            content: 'Go back to view or edit the question paper and marking rules.',
            disableBeacon: true,
        },
        {
            target: '.tour-list-filter',
            title: 'Filter Results',
            content: 'Filter the list by status (processing, completed, etc.) to organize your view.',
            disableBeacon: true,
        },
        {
            target: '.tour-list-table',
            title: 'Result List',
            content: 'View scores and access detailed reports for each student here.',
            disableBeacon: true,
        }
    ];

    const tourSteps = isSettingsMode || selectedPaper?.status === 'pending' ? setupTourSteps : listTourSteps;

    useEffect(() => {
        if (!selectedPaper) return;

        const isSetupMode = selectedPaper.status === 'pending' || isSettingsMode;

        if (isSetupMode) {
            const hasSeenSetup = localStorage.getItem('hasSeenEvaluatorSetupTour');
            if (!hasSeenSetup) setRunTour(true);
        } else {
            const hasSeenList = localStorage.getItem('hasSeenEvaluatorListTour');
            if (!hasSeenList) setRunTour(true);
        }
    }, [selectedPaper, isSettingsMode]);

    const handleJoyrideCallback = (data: CallBackProps) => {
        const { status, action } = data;
        const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status) || action === ACTIONS.CLOSE) {
            setRunTour(false);
            const isSetupMode = selectedPaper?.status === 'pending' || isSettingsMode;
            if (isSetupMode) {
                localStorage.setItem('hasSeenEvaluatorSetupTour', 'true');
            } else {
                localStorage.setItem('hasSeenEvaluatorListTour', 'true');
            }
        }
    };

    if (loading && papers.length === 0) {
        return (
            <div className="flex items-center justify-center h-screen bg-muted/20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!selectedPaper) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <h2 className="text-xl font-bold text-gray-900">Paper Not Found</h2>
                    <button
                        onClick={() => navigate('/evaluator')}
                        className="mt-4 text-primary hover:underline"
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    const handleStartEvaluation = () => {
        dispatch(updatePaperStatus({ id: selectedPaper.id, status: 'in-progress' }));
    };

    const handleViewReport = (sheetId: string) => {
        navigate(`/evaluator/${selectedPaper.id}/report/${sheetId}`);
    };


    return (
        <div className="min-h-full bg-muted/20">
            <div className="flex  max-w-7xl mx-auto items-center w-full pt-2 sticky top-0 z-20">
                <EvaluatorHeader selectedPaper={selectedPaper} onTutorialClick={() => setRunTour(true)} />
            </div>

            <div className="p-2 max-w-7xl mx-auto space-y-4">
                {selectedPaper.status === 'pending' ? (
                    <EvaluatorSetup
                        paper={selectedPaper}
                        initialRules={selectedPaper.strictnessRules || ''}
                        onStartEvaluation={handleStartEvaluation}
                    />
                ) : isSettingsMode ? (
                    <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                        <button
                            onClick={() => setSearchParams({})}
                            className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 w-fit px-3 py-2 rounded-lg hover:bg-white border border-transparent hover:border-gray-200 transition-all hover:shadow-sm"
                        >
                            <ArrowLeft className="w-4 h-4" /> Back to List
                        </button>
                        <EvaluatorSetup
                            paper={selectedPaper}
                            mode="edit"
                            initialRules={selectedPaper.strictnessRules || ''}
                            onStartEvaluation={() => setSearchParams({})}
                            showBackButton={false}
                        />
                    </div>
                ) : (
                    <AnswerSheetList
                        totalCopies={selectedPaper.totalCopies}
                        paperId={selectedPaper.id}
                        paperCode={selectedPaper.code}
                        subjectName={selectedPaper.subject}
                        onViewDetails={handleViewReport}
                        onSettingsClick={() => setSearchParams({ view: "settings" })}
                    />
                )}
            </div>


            <Joyride
                steps={tourSteps}
                run={runTour}
                continuous
                showProgress
                showSkipButton
                tooltipComponent={TourTooltip}
                callback={handleJoyrideCallback}
                disableScrollParentFix={true}
                disableScrolling={true}
                floaterProps={{
                    disableAnimation: true,
                }}
                styles={{
                    spotlight: {
                        borderRadius: '16px',
                    },
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.65)',
                    },
                    options: {
                        zIndex: 10000,
                    }
                }}
            />
        </div >
    );
};

export default EvaluatorDetails;
